import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import type { Alert, AlertDetail, Comparator, Severity, WorkType } from "../types";
import { fmtMonth, fmtPct, fmtUnits, timeAgo } from "../format";

// ── shared bits ─────────────────────────────────────────────────────────────

export const SEVERITY_LABEL: Record<Severity, string> = { high: "High", medium: "Medium", low: "Low" };
const SEVERITY_RANK: Record<Severity, number> = { high: 0, medium: 1, low: 2 };

/** Alerts that still want a planner's attention. */
export const needsAttention = (a: Alert) => a.status === "open" || a.status === "acknowledged";

export function conditionText(comparator: Comparator, threshold: number | null): string {
  if (threshold == null || Number.isNaN(threshold)) return "";
  return comparator === "above" ? `above ${fmtPct(threshold)}`
    : comparator === "below" ? `below ${fmtPct(threshold)}`
    : `beyond ±${Math.abs(threshold)}%`;
}

export function periodText(periods: string[]): string {
  if (!periods.length) return "recent closed months";
  return periods.length === 1 ? fmtMonth(periods[0]) : `${fmtMonth(periods[0])} – ${fmtMonth(periods[periods.length - 1])}`;
}

/** Warning triangle (high), diamond (medium) or info dot (low): shape as well as colour. */
export function SeverityIcon({ severity, size = 14 }: { severity: Severity; size?: number }) {
  const common = { width: size, height: size, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor",
                   strokeWidth: 1.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  return (
    <span className={`sev-icon sev-${severity}`} role="img" aria-label={`${SEVERITY_LABEL[severity]} severity`}
          title={`${SEVERITY_LABEL[severity]} severity`}>
      {severity === "high" ? (
        <svg {...common}><path d="M8 2.2 14.2 13H1.8z" /><path d="M8 6.4v3.2" /><path d="M8 11.4v.01" /></svg>
      ) : severity === "medium" ? (
        <svg {...common}><path d="M8 1.8 14.2 8 8 14.2 1.8 8z" /><path d="M8 5.4v3.4" /><path d="M8 10.8v.01" /></svg>
      ) : (
        <svg {...common}><circle cx="8" cy="8" r="6" /><path d="M8 7.4v3.6" /><path d="M8 5.2v.01" /></svg>
      )}
    </span>
  );
}

export function BellIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6}
         strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 11.5V7a4 4 0 0 1 8 0v4.5l1.2 1.3H2.8z" /><path d="M6.6 14a1.5 1.5 0 0 0 2.8 0" />
    </svg>
  );
}

const STATUS_TEXT: Record<Alert["status"], string> = {
  open: "New", acknowledged: "Seen", snoozed: "Snoozed", converted: "Action created",
  dismissed: "Dismissed", resolved: "Resolved",
};

// ── canvas stack ────────────────────────────────────────────────────────────

interface StackProps {
  boardId: string;
  alerts: Alert[];
  open: boolean;
  workTypes: Record<string, WorkType>;
  onToggle: () => void;
  onChanged: () => void;
  /** A card was created from alerts: reload intents and point the planner at it. */
  onCreated: (intentId: string, ran: boolean) => void;
}

/** A bucket's alerts: one pill (count + severity) that expands into the list, grouped by rule. */
export function AlertStack({ boardId, alerts, open, workTypes, onToggle, onChanged, onCreated }: StackProps) {
  const live = alerts.filter(needsAttention);
  const handled = alerts.filter((a) => !needsAttention(a));
  const worst = live.reduce<Severity | null>((w, a) => (!w || SEVERITY_RANK[a.severity] < SEVERITY_RANK[w] ? a.severity : w), null);
  const bySev = (["high", "medium", "low"] as Severity[]).map((s) => [s, live.filter((a) => a.severity === s).length] as const)
    .filter(([, n]) => n > 0);
  const fresh = live.some((a) => a.status === "open");
  const label = live.length
    ? `${live.length} alert${live.length === 1 ? "" : "s"} need${live.length === 1 ? "s" : ""} attention`
    : `${handled.length} handled alert${handled.length === 1 ? "" : "s"}`;

  return (
    <div className="alert-stack">
      <button type="button" className={`alert-pill${live.length ? ` sev-${worst}` : " quiet"}${fresh ? " fresh" : ""}`}
              aria-expanded={open} onClick={onToggle} aria-label={label} title={label}>
        <span className="alert-pill-bell"><BellIcon /></span>
        <b>{live.length || handled.length}</b>
        <span className="alert-pill-text">{live.length ? `alert${live.length === 1 ? "" : "s"}` : "handled"}</span>
        {bySev.map(([s, n]) => (
          <span key={s} className={`alert-pill-sev sev-${s}`}><SeverityIcon severity={s} size={12} />{n}</span>
        ))}
        <span className="alert-pill-chev" aria-hidden>{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <AlertPanel boardId={boardId} live={live} handled={handled} workTypes={workTypes}
                    onChanged={onChanged} onCreated={onCreated} />
      )}
    </div>
  );
}

const stopAll = {
  onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
  onClick: (e: React.MouseEvent) => e.stopPropagation(),
  onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation(),
};

function AlertPanel({ boardId, live, handled, workTypes, onChanged, onCreated }: {
  boardId: string; live: Alert[]; handled: Alert[]; workTypes: Record<string, WorkType>;
  onChanged: () => void; onCreated: (intentId: string, ran: boolean) => void;
}) {
  const [showHandled, setShowHandled] = useState(false);
  const groups = useMemo(() => {
    const m = new Map<string, Alert[]>();
    for (const a of live) m.set(a.rule_id, [...(m.get(a.rule_id) ?? []), a]);
    return [...m.values()].sort((x, y) => SEVERITY_RANK[x[0].severity] - SEVERITY_RANK[y[0].severity]);
  }, [live]);

  return (
    <div className="canvas-overlay alert-panel" {...stopAll}>
      {groups.length === 0 && <div className="small muted">Nothing needs attention here.</div>}
      {groups.map((g) => (
        <RuleGroup key={g[0].rule_id} boardId={boardId} alerts={g} workTypes={workTypes}
                   onChanged={onChanged} onCreated={onCreated} />
      ))}
      {handled.length > 0 && (
        <div className="alert-handled">
          <button type="button" className="card-action" onClick={() => setShowHandled((v) => !v)} aria-expanded={showHandled}>
            {showHandled ? "▴" : "▾"} {handled.length} handled
          </button>
          {showHandled && handled.map((a) => (
            <div key={a.id} className="alert-row handled">
              <SeverityIcon severity={a.severity} size={12} />
              <span className="mono small">{a.subject_label}</span>
              <span className="small muted grow">{a.rule_name}</span>
              <span className="small muted">{STATUS_TEXT[a.status]}{a.intent_id ? ` → ${a.intent_id}` : ""}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RuleGroup({ boardId, alerts, workTypes, onChanged, onCreated }: {
  boardId: string; alerts: Alert[]; workTypes: Record<string, WorkType>;
  onChanged: () => void; onCreated: (intentId: string, ran: boolean) => void;
}) {
  const first = alerts[0];
  const [picked, setPicked] = useState<Set<string>>(() => new Set(alerts.map((a) => a.id)));
  const [detail, setDetail] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);
  const proposal = alerts.find((a) => a.proposal)?.proposal;
  const [text, setText] = useState(proposal?.text ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { if (!preparing) setText(proposal?.text ?? ""); }, [proposal?.text, preparing]);

  const toggle = (id: string) => setPicked((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const ids = alerts.filter((a) => picked.has(a.id)).map((a) => a.id);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError("");
    try { await fn(); onChanged(); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const convert = (run: boolean) => act(async () => {
    const r = await api.convertAlerts(boardId, ids, text.trim() === proposal?.text ? "" : text.trim(), run);
    setPreparing(false);
    onCreated(r.intent.id, run);
  });
  const wt = proposal ? workTypes[proposal.work_type] : undefined;

  return (
    <section className={`alert-group sev-${first.severity}`} aria-label={first.rule_name}>
      <div className="alert-group-head">
        <SeverityIcon severity={first.severity} />
        <b className="grow">{first.rule_name}</b>
        <span className="small muted">{alerts.length} · {conditionText(first.comparator, first.threshold)}</span>
      </div>
      {alerts.map((a) => (
        <div key={a.id}>
          <div className={`alert-row${a.status === "open" ? " is-new" : ""}`}>
            <input type="checkbox" checked={picked.has(a.id)} onChange={() => toggle(a.id)}
                   aria-label={`Include ${a.subject_label} in the action`} />
            <span className="mono small alert-subject" title={a.subject_label}>{a.subject_label}</span>
            <span className="small muted grow">{periodText(a.periods)}</span>
            <span className={`alert-value ${a.value >= 0 ? "up" : "down"}`} title="Worst month">{fmtPct(a.value)}</span>
            <button type="button" className="card-action" aria-expanded={detail === a.id}
                    onClick={() => setDetail((d) => (d === a.id ? null : a.id))} title="Month by month, from Kinaxis now">
              {detail === a.id ? "▴" : "▾"}</button>
            {a.status === "open" && (
              <button type="button" className="card-action" disabled={busy} title="Mark as seen"
                      onClick={() => act(() => api.setAlertStatus(boardId, a.id, "acknowledged"))}>✓</button>
            )}
            <button type="button" className="card-action" disabled={busy} title="Snooze for a day"
                    onClick={() => act(() => api.setAlertStatus(boardId, a.id, "snoozed", 24))}>⏾</button>
            <button type="button" className="card-action" disabled={busy} title="Dismiss until it clears"
                    onClick={() => act(() => api.setAlertStatus(boardId, a.id, "dismissed"))}>✕</button>
          </div>
          {detail === a.id && <AlertMonths boardId={boardId} alertId={a.id} />}
        </div>
      ))}
      <div className="alert-proposal">
        {!preparing ? (
          <>
            <div className="small">
              <span className="muted">Ready action{proposal?.method === "llm" ? " · drafted by Claude" : proposal ? " · standard response" : ""}: </span>
              {proposal ? proposal.text : <span className="muted">drafted when it is converted</span>}
            </div>
            <div className="row">
              {wt && <span className="small muted grow">{wt.label}{proposal?.rationale ? ` — ${proposal.rationale}` : ""}</span>}
              {!wt && <span className="grow" />}
              <button type="button" className="btn primary sm" disabled={!ids.length || busy} onClick={() => setPreparing(true)}>
                Review action card{ids.length !== alerts.length ? ` (${ids.length})` : ""}
              </button>
            </div>
          </>
        ) : (
          <>
            <label className="field">
              <span>Action card for {ids.length} alert{ids.length === 1 ? "" : "s"}{wt ? ` · ${wt.label}` : ""}</span>
              <textarea className="input" rows={3} value={text} onChange={(e) => setText(e.target.value)} />
            </label>
            <div className="small muted">The card is scoped to exactly the alerts you ticked. Nothing changes in Kinaxis until it runs and is pushed.</div>
            <div className="row">
              <button type="button" className="btn ghost sm" disabled={busy} onClick={() => setPreparing(false)}>Back</button>
              <span className="grow" />
              <button type="button" className="btn sm" disabled={busy || !text.trim()} onClick={() => convert(false)}>Add card</button>
              <button type="button" className="btn primary sm" disabled={busy || !text.trim()} onClick={() => convert(true)}>Add &amp; run</button>
            </div>
          </>
        )}
        {error && <div className="small error-text">{error}</div>}
      </div>
    </section>
  );
}

function AlertMonths({ boardId, alertId }: { boardId: string; alertId: string }) {
  const [d, setD] = useState<AlertDetail | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    api.alertDetail(boardId, alertId).then((r) => live && setD(r)).catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [boardId, alertId]);
  if (error) return <div className="small error-text">{error}</div>;
  if (!d) return <div className="row small muted"><div className="spinner" /> Reading from Kinaxis…</div>;
  const m = d.measure;
  return (
    <table className="data compact alert-months">
      <thead><tr><th>{m.rows[0]?.period ? "Month" : "Window"}</th><th className="num">{m.current}</th>
        <th className="num">{m.reference}</th><th className="num">{m.label.includes("risk") ? "At risk" : "Diff"}</th></tr></thead>
      <tbody>{m.rows.map((r) => (
        <tr key={r.period || "all"} className={r.breached ? "breached" : ""}>
          <td>{r.period ? fmtMonth(r.period) : "Closed months"}</td>
          <td className="num">{fmtUnits(r.current)}</td><td className="num">{fmtUnits(r.reference)}</td>
          <td className="num">{fmtPct(r.value)}</td>
        </tr>
      ))}</tbody>
      <caption className="small muted">Detected {timeAgo(d.alert.detected_at)} · last seen {timeAgo(d.alert.last_seen_at)}</caption>
    </table>
  );
}
